import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { useMe } from "../components/Shell.tsx";
import { Sticker } from "../components/Sticker.tsx";
import { api, call, errorMessage } from "../lib/api.ts";
import { money } from "../lib/format.ts";
import { toast } from "../lib/toast.ts";

function regionName(code: string): string {
  try {
    return (
      new Intl.DisplayNames([navigator.language || "en", "en"], { type: "region" }).of(code) ?? code
    );
  } catch {
    return code;
  }
}

const STATUS_LABEL: Record<string, string> = {
  paid: "Paid",
  in_production: "Printing",
  shipped: "Shipped",
  delivered: "Delivered",
  refunded: "Refunded",
  failed: "Failed",
  canceled: "Canceled",
};

function SignIn() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const m = useMutation({
    mutationFn: () =>
      call(api.auth["magic-link"].$post({ json: { email, redirectTo: "/account" } })),
    onSuccess: () => setSent(true),
  });
  return (
    <div className="sheet sheet--narrow signin">
      <Sticker face="var(--white)" tilt={-1} className="signin__sticker">
        <div className="signin__body">
          {sent ? (
            <>
              <Icon name="mail" size={32} />
              <h1>Check your inbox</h1>
              <p>
                We sent a sign-in link to <strong>{email}</strong>. It works once and expires in 20
                minutes.
              </p>
              <button type="button" className="link-btn" onClick={() => setSent(false)}>
                Use a different email
              </button>
            </>
          ) : (
            <>
              <h1>Your orders &amp; designs</h1>
              <p>No password. We'll email you a one-time sign-in link.</p>
              <form
                className="stack"
                onSubmit={(e) => {
                  e.preventDefault();
                  m.mutate();
                }}
              >
                <div className="field">
                  <label htmlFor="si-email">Email</label>
                  <input
                    id="si-email"
                    className="input"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                {m.isError && <p className="form-error">{errorMessage(m.error)}</p>}
                <button type="submit" className="btn btn--block" disabled={m.isPending}>
                  {m.isPending && <span className="spinner" aria-hidden="true" />} Email me a link
                </button>
              </form>
            </>
          )}
        </div>
      </Sticker>
    </div>
  );
}

export function AccountPage() {
  const me = useMe();
  const qc = useQueryClient();
  const designs = useQuery({
    queryKey: ["my-designs"],
    queryFn: () => call(api.me.designs.$get()),
    enabled: Boolean(me.data?.user),
  });
  const orders = useQuery({
    queryKey: ["my-orders"],
    queryFn: () => call(api.me.orders.$get()),
    enabled: Boolean(me.data?.user),
  });
  const address = useQuery({
    queryKey: ["my-address"],
    queryFn: () => call(api.me.address.$get()),
    enabled: Boolean(me.data?.user),
  });
  const forget = useMutation({
    mutationFn: () => call(api.me.address.$delete()),
    onSuccess: () => {
      qc.setQueryData(["my-address"], { address: null });
      toast("Address removed");
    },
    onError: (err) => toast(errorMessage(err), { tone: "error" }),
  });
  const saved = address.data?.address;

  if (me.isLoading)
    return (
      <div className="sheet">
        <div className="skeleton" style={{ height: 300 }} />
      </div>
    );
  const user = me.data?.user;
  if (!user) return <SignIn />;

  return (
    <div className="sheet account">
      <header className="account__head">
        <div>
          <h1 className="page-title">Hi{user.name ? `, ${user.name}` : ""}</h1>
          <p className="muted">{user.email}</p>
        </div>
        <div className="account__actions">
          {user.role === "admin" && (
            <Link to="/admin" className="btn btn--white btn--sm">
              Admin
            </Link>
          )}
          <button
            type="button"
            className="btn btn--white btn--sm"
            onClick={async () => {
              await call(api.auth.logout.$post());
              await qc.invalidateQueries();
              toast("Signed out");
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      <section className="section--tight" aria-labelledby="acc-orders">
        <h2 id="acc-orders" className="account__h">
          Orders
        </h2>
        {orders.data?.orders.length ? (
          <ul className="orderlist">
            {orders.data.orders.map((o) => (
              <li key={o.id}>
                <Link to={`/orders/${o.id}`} className="orderrow">
                  <span className="orderrow__thumbs">
                    {o.items
                      .slice(0, 3)
                      .map((i) =>
                        i.previewUrl ? <img key={i.id} src={i.previewUrl} alt="" /> : null,
                      )}
                  </span>
                  <span className="orderrow__main">
                    <strong>{o.number}</strong>
                    <span className="muted">{new Date(o.createdAt).toLocaleDateString()}</span>
                  </span>
                  <span
                    className="badge"
                    data-tone={o.status === "delivered" || o.status === "shipped" ? "good" : "info"}
                  >
                    {STATUS_LABEL[o.status] ?? o.status}
                  </span>
                  <span className="price tabular">{money(o.totalCents, o.currency)}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{orders.isLoading ? "Loading…" : "No orders yet."}</p>
        )}
      </section>

      <section className="section--tight" aria-labelledby="acc-address">
        <h2 id="acc-address" className="account__h">
          Shipping address
        </h2>
        {saved ? (
          <div className="savedaddr">
            <address>
              {saved.firstName} {saved.lastName}
              <br />
              {saved.addressLine1}
              {saved.addressLine2 && (
                <>
                  <br />
                  {saved.addressLine2}
                </>
              )}
              <br />
              {saved.postCode} {saved.city}
              {saved.state && `, ${saved.state}`}
              <br />
              {regionName(saved.country)}
              {saved.phone && (
                <>
                  <br />
                  {saved.phone}
                </>
              )}
            </address>
            <button
              type="button"
              className="btn btn--white btn--sm"
              disabled={forget.isPending}
              onClick={() => forget.mutate()}
            >
              Remove
            </button>
          </div>
        ) : (
          <p className="muted">
            {address.isLoading
              ? "Loading…"
              : "None saved yet. Tick “Remember this address” at checkout and we'll fill it in next time."}
          </p>
        )}
      </section>

      <section className="section--tight" aria-labelledby="acc-designs">
        <h2 id="acc-designs" className="account__h">
          Your designs
        </h2>
        <div className="designgrid">
          {designs.data?.designs.map((d, i) => (
            <Link key={d.id} to={`/d/${d.shareId}`} className="designcard">
              <Sticker face="var(--white)" tilt={i % 2 ? 1.5 : -1.5} size="sm">
                {d.previewUrl && <img src={d.previewUrl} alt="" />}
              </Sticker>
              <span>{d.title}</span>
            </Link>
          ))}
          <Link to="/make" className="makeslot makeslot--sm">
            <span className="makeslot__plus">
              <Icon name="plus" size={24} />
            </span>
            <span className="makeslot__title">New design</span>
          </Link>
        </div>
      </section>
    </div>
  );
}

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = new URLSearchParams(window.location.hash.slice(1)).get("token");
    history.replaceState(null, "", window.location.pathname);
    if (!token) {
      setError("This sign-in link is incomplete.");
      return;
    }
    call(api.auth.verify.$post({ json: { token } }))
      .then(async (res) => {
        await qc.invalidateQueries();
        toast(`Signed in as ${res.user.email}`);
        navigate(res.redirectTo || "/account", { replace: true });
      })
      .catch((err) => setError(errorMessage(err)));
  }, [navigate, qc]);
  return (
    <div className="sheet sheet--narrow">
      {error ? (
        <>
          <h1 className="page-title">That link didn't work</h1>
          <p className="lede">{error}</p>
          <Link to="/account" className="btn" style={{ marginTop: "2rem" }}>
            Get a new link
          </Link>
        </>
      ) : (
        <p className="lede">
          <span className="spinner" aria-hidden="true" /> Signing you in…
        </p>
      )}
    </div>
  );
}
